<template>
  <div class="vin-filter-panel" v-loading="loading">
    <section class="rule-type-switch" aria-label="VIN 规则类型">
      <div class="rule-type-switch-copy">
        <strong>规则类型</strong>
        <span>精确结构规则用于完整 VIN 字段校验；无效 VIN 掩码用于过滤已知的无效在线数据。</span>
      </div>
      <el-radio-group v-model="activeRuleTab" size="large" aria-label="选择 VIN 规则类型">
        <el-radio-button label="structured">精确结构规则</el-radio-button>
        <el-radio-button label="mask">无效 VIN 掩码</el-radio-button>
      </el-radio-group>
    </section>

    <template v-if="activeRuleTab === 'structured'">
    <section class="panel-header">
      <div>
        <h3>新增 VIN 过滤规则</h3>
        <p>Redis：FordServer:VinFilter:Rules</p>
      </div>

      <div class="header-actions">
        <el-switch
          v-model="syncCopy"
          inline-prompt
          active-text="同步"
          inactive-text="本机"
        />
        <el-button :icon="Plus" type="primary" plain @click="startCreate">
          新增
        </el-button>
        <el-button :icon="Refresh" plain :loading="loading" @click="fetchRules">
          刷新
        </el-button>
      </div>
    </section>

    <section class="config-section rule-list-section">
      <div class="section-heading">
        <div>
          <h4>规则列表</h4>
          <p>{{ listHint }}</p>
        </div>
        <el-tag effect="plain">{{ rules.length }}</el-tag>
      </div>

      <el-table
        :data="sortedRules"
        border
        stripe
        size="small"
        class="rule-table"
        empty-text="暂无 VIN 规则"
        highlight-current-row
        @row-click="selectRule"
      >
        <el-table-column label="状态" width="74" align="center">
          <template #default="{ row }">
            <el-tag :type="row.enabled === false ? 'info' : 'success'" effect="light">
              {{ row.enabled === false ? '停用' : '启用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="规则名称" min-width="180" show-overflow-tooltip>
          <template #default="{ row }">
            <div class="rule-name-cell">
              <strong>{{ row.name || row.id }}</strong>
              <span>{{ row.id }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="第 1-3 位" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="mono">{{ listText(row.wmiAllowList) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="第 4-8 位数量" width="122" align="center">
          <template #default="{ row }">{{ vehicleDescriptionCount(row) }}</template>
        </el-table-column>
        <el-table-column prop="priority" label="优先级" width="82" align="center" />
        <el-table-column label="操作" width="130" align="center" fixed="right">
          <template #default="{ row }">
            <el-tooltip content="编辑规则" placement="top">
              <el-button :icon="EditPen" link type="primary" aria-label="编辑规则" @click.stop="selectRule(row)" />
            </el-tooltip>
            <el-tooltip content="复制规则" placement="top">
              <el-button :icon="CopyDocument" link type="primary" aria-label="复制规则" @click.stop="cloneRule(row)" />
            </el-tooltip>
            <el-popconfirm
              title="确认删除该 VIN 规则？"
              confirm-button-text="删除"
              cancel-button-text="取消"
              @confirm="deleteRule(row)"
            >
              <template #reference>
                <el-tooltip content="删除规则" placement="top">
                  <el-button
                    :icon="Delete"
                    link
                    type="danger"
                    aria-label="删除规则"
                    :disabled="row.persisted !== true"
                    @click.stop
                  />
                </el-tooltip>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <section class="config-section">
      <div class="section-heading">
        <div>
          <h4>基础信息</h4>
          <p>规则编号、名称、优先级、状态和备注。</p>
        </div>
        <el-tag :type="form.enabled ? 'success' : 'info'" effect="plain">
          {{ form.enabled ? '启用' : '停用' }}
        </el-tag>
      </div>

      <el-form label-position="top" class="rule-form">
        <div class="field-grid">
          <el-form-item label="规则编号">
            <el-input
              v-model.trim="form.id"
              clearable
              :disabled="editingPersisted"
              placeholder="jiangling-vin-filter"
            />
          </el-form-item>
          <el-form-item label="规则名称">
            <el-input v-model.trim="form.name" clearable placeholder="江铃 VIN 过滤模板" />
          </el-form-item>
          <el-form-item label="优先级">
            <el-input-number v-model="form.priority" :min="0" :max="9999" class="full-control" />
          </el-form-item>
          <el-form-item label="状态">
            <el-switch v-model="form.enabled" active-text="启用" inactive-text="停用" />
          </el-form-item>
        </div>

        <el-form-item label="备注">
          <el-input
            v-model="form.remark"
            type="textarea"
            :rows="3"
            resize="vertical"
            placeholder="填写数据来源、变更原因或维护说明"
          />
        </el-form-item>
      </el-form>
    </section>

    <div class="position-grid">
      <section class="config-section position-card manufacturer-card">
        <div class="section-heading">
          <div>
            <h4>第 1-3 位：厂商识别码</h4>
            <p>用于识别车辆制造厂商或品牌来源，例如 LJX、LEF。</p>
          </div>
          <el-tag effect="plain">{{ form.manufacturerCodes.length }}</el-tag>
        </div>

        <div class="tag-editor">
          <el-tag
            v-for="code in form.manufacturerCodes"
            :key="code"
            closable
            effect="plain"
            @close="removeManufacturerCode(code)"
          >
            {{ code }}
          </el-tag>
          <el-input
            v-model="manufacturerInput"
            class="tag-input"
            placeholder="+ 添加"
            maxlength="3"
            @keyup.enter="addManufacturerCode"
          />
          <el-button :icon="Plus" plain @click="addManufacturerCode">
            添加
          </el-button>
        </div>
      </section>

      <section class="config-section position-card vehicle-card">
        <div class="section-heading">
          <div>
            <h4>第 4-8 位：车辆描述码</h4>
            <p>按厂商识别码分别维护车辆描述码白名单。</p>
          </div>
          <el-tag effect="plain">{{ vehicleDescriptionRows.length }}</el-tag>
        </div>

        <el-table
          :data="vehicleDescriptionRows"
          border
          size="small"
          class="rule-table"
          empty-text="请先维护第 1-3 位厂商识别码"
        >
          <el-table-column prop="manufacturerCode" label="厂商识别码" width="112" />
          <el-table-column prop="count" label="车辆描述码数量" width="132" align="center" />
          <el-table-column prop="preview" label="车辆描述码预览" min-width="220" show-overflow-tooltip />
          <el-table-column label="操作" width="210" align="center">
            <template #default="{ row }">
              <el-button link type="primary" @click="openVehicleDialog(row.manufacturerCode)">
                编辑
              </el-button>
              <el-button link type="primary" @click="openVehicleDialog(row.manufacturerCode, true)">
                批量导入
              </el-button>
              <el-button link type="danger" @click="clearVehicleDescriptions(row.manufacturerCode)">
                清空
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <section class="config-section position-card">
        <div class="section-heading">
          <div>
            <h4>第 9 位：校验位</h4>
            <p>普通模式不需要维护正则。</p>
          </div>
        </div>
        <div class="readonly-rule">
          <strong>规则：允许数字 0-9 或 X</strong>
        </div>
      </section>

      <section class="config-section position-card year-card">
        <div class="section-heading">
          <div>
            <h4>第 10 位：年款码</h4>
            <p>根据起始年份和结束年份自动生成允许的年款码。</p>
          </div>
        </div>
        <div class="year-controls">
          <el-input-number v-model="form.yearFrom" :min="1980" :max="2039" />
          <span>至</span>
          <el-input-number v-model="form.yearTo" :min="1980" :max="2039" />
        </div>
        <div class="year-code-strip">
          <el-tag
            v-for="item in yearEntries"
            :key="item.year"
            effect="plain"
            size="small"
          >
            {{ item.year }}={{ item.code }}
          </el-tag>
        </div>
      </section>

      <section class="config-section position-card position-card-wide">
        <div class="section-heading">
          <div>
            <h4>第 11 位：装配厂代码</h4>
            <p>使用多选维护允许的装配厂代码。</p>
          </div>
          <el-button :icon="Plus" plain size="small" @click="plantDialog.visible = true">
            其他装配厂代码
          </el-button>
        </div>

        <div v-if="form.assemblyPlants.length" class="plant-options">
          <el-checkbox
            v-for="plant in form.assemblyPlants"
            :key="plant.code"
            v-model="plant.selected"
            border
          >
            {{ plant.code }} - {{ plant.name }}
          </el-checkbox>
        </div>
        <el-empty v-else class="plant-empty" description="当前规则未维护装配厂代码" :image-size="54" />
      </section>

      <section class="config-section position-card serial12-card">
        <div class="section-heading">
          <div>
            <h4>第 12 位：流水号首位</h4>
            <p>默认不限制，确认能稳定区分目标车辆时再启用。</p>
          </div>
        </div>

        <el-checkbox v-model="form.serial12Enabled">
          启用限制
        </el-checkbox>

        <div v-if="form.serial12Enabled" class="tag-editor compact-tags">
          <el-tag
            v-for="code in form.serial12AllowList"
            :key="code"
            closable
            effect="plain"
            @close="removeSerial12Code(code)"
          >
            {{ code }}
          </el-tag>
          <el-input
            v-model="serial12Input"
            class="tag-input tag-input-small"
            placeholder="+ 添加"
            maxlength="1"
            @keyup.enter="addSerial12Code"
          />
          <el-button :icon="Plus" plain @click="addSerial12Code" />
        </div>

        <div v-else class="readonly-rule">
          <strong>第 12 位不参与过滤。</strong>
        </div>
      </section>

      <section class="config-section position-card serial-tail-card">
        <div class="section-heading">
          <div>
            <h4>第 13-17 位：流水号后五位</h4>
            <p>普通模式不需要维护正则。</p>
          </div>
        </div>
        <div class="readonly-rule">
          <strong>规则：必须是 5 位数字</strong>
        </div>
      </section>
    </div>

    <section class="config-section test-section">
      <div class="section-heading">
        <div>
          <h4>规则测试</h4>
          <p>请输入 17 位 VIN，系统会按位显示匹配结果。</p>
        </div>
        <el-button :icon="VideoPlay" type="success" @click="runVinTest">
          测试
        </el-button>
      </div>

      <div class="test-layout">
        <el-input
          v-model="testVin"
          clearable
          placeholder="LJXCU2BBXTTA12345"
          @keyup.enter="runVinTest"
        />
        <el-tag v-if="testResult" :type="testResultTagType" effect="light">
          {{ testResult.passed ? '通过' : '不通过' }}
        </el-tag>
      </div>

      <el-table
        v-if="testResult"
        :data="testResult.items"
        border
        stripe
        size="small"
        class="test-table"
      >
        <el-table-column prop="position" label="位置" width="118" />
        <el-table-column prop="name" label="中文含义" width="132" />
        <el-table-column prop="value" label="值" width="96" />
        <el-table-column label="结果" width="78" align="center">
          <template #default="{ row }">
            <el-tag :type="row.passed ? 'success' : 'danger'" effect="light">
              {{ row.passed ? '通过' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="message" label="说明" min-width="260" show-overflow-tooltip />
      </el-table>

      <div v-if="testResult?.errors?.length" class="error-list">
        <el-alert
          v-for="error in testResult.errors"
          :key="error"
          :title="error"
          type="error"
          :closable="false"
          show-icon
        />
      </div>
    </section>

    <section class="config-section generated-section">
      <div class="section-heading">
        <div>
          <h4>自动生成结果</h4>
          <p>正则和 JSON 由结构化规则自动生成，仅用于预览、导出或排查。</p>
        </div>
      </div>

      <el-collapse v-model="activeGeneratedSections">
        <el-collapse-item v-if="advanced.showRegexPreview" title="正则预览" name="regex">
          <div class="preview-toolbar">
            <span>只读预览，不建议手工修改。</span>
            <el-button :icon="CopyDocument" plain size="small" @click="copyText(regexPreview, '正则已复制')">
              复制
            </el-button>
          </div>
          <pre class="preview-box">{{ regexPreview }}</pre>
        </el-collapse-item>
        <el-collapse-item v-if="advanced.showJsonPreview" title="JSON 配置预览" name="json">
          <div class="preview-toolbar">
            <span>保存时使用该结构化 JSON。</span>
            <el-button :icon="CopyDocument" plain size="small" @click="copyText(jsonPreview, 'JSON 已复制')">
              复制
            </el-button>
          </div>
          <pre class="preview-box">{{ jsonPreview }}</pre>
        </el-collapse-item>
      </el-collapse>
    </section>

    <section class="config-section advanced-section">
      <el-collapse v-model="activeAdvancedSections">
        <el-collapse-item title="高级设置" name="advanced">
          <div class="advanced-grid">
            <el-form-item label="第 9 位校验位规则">
              <el-input v-model.trim="form.checkDigitPattern" />
            </el-form-item>
            <el-form-item label="第 13-17 位流水号后五位规则">
              <el-input v-model.trim="form.serialTailPattern" />
            </el-form-item>
            <el-form-item label="允许某个厂商识别码下车辆描述码为空">
              <el-switch v-model="form.allowAnyVdsWhenListMissing" />
            </el-form-item>
            <el-form-item label="导出正则">
              <el-switch v-model="advanced.showRegexPreview" />
            </el-form-item>
            <el-form-item label="显示 JSON 配置">
              <el-switch v-model="advanced.showJsonPreview" />
            </el-form-item>
          </div>
        </el-collapse-item>
      </el-collapse>
    </section>

    <div class="save-bar">
      <el-button :icon="DocumentChecked" type="primary" :loading="saving" @click="saveRule">
        保存规则
      </el-button>
    </div>

    <el-dialog v-model="vehicleDialog.visible" :title="vehicleDialogTitle" width="min(920px, 94vw)">
      <div class="dialog-body">
        <div class="tag-editor">
          <el-tag
            v-for="code in vehicleDialog.draftCodes"
            :key="code"
            closable
            effect="plain"
            @close="removeVehicleDialogCode(code)"
          >
            {{ code }}
          </el-tag>
          <el-input
            v-model="vehicleDialog.newCode"
            class="tag-input"
            maxlength="5"
            placeholder="+ 添加"
            @keyup.enter="addVehicleDialogCode"
          />
          <el-button :icon="Plus" plain @click="addVehicleDialogCode">
            添加
          </el-button>
        </div>

        <el-form label-position="top">
          <el-form-item label="批量粘贴">
            <p class="field-help">
              可一行一个，也可使用空格、逗号、中文逗号或分号分隔。粘贴后点击“解析”，系统会自动大写、去重并提示非法项。
            </p>
            <el-input
              v-model="vehicleDialog.bulkText"
              type="textarea"
              :rows="5"
              resize="vertical"
              placeholder="示例：&#10;CU2BB&#10;CU2BM&#10;CU2BN, CU2BP"
            />
          </el-form-item>
        </el-form>

        <el-table
          :data="vehiclePromptRows"
          border
          size="small"
          class="prompt-table"
          empty-text="暂无第 4-8 位码"
        >
          <el-table-column prop="code" label="第 4-8 位" width="112" />
          <el-table-column
            v-for="lang in promptLanguages"
            :key="lang.key"
            :label="lang.label"
            min-width="190"
          >
            <template #default="{ row }">
              <el-input
                v-model="vehicleDialog.promptDrafts[row.code][lang.key]"
                type="textarea"
                :rows="2"
                resize="vertical"
                maxlength="4096"
                show-word-limit
                placeholder="设备显示文本"
              />
            </template>
          </el-table-column>
        </el-table>

        <div class="dialog-actions">
          <el-button plain @click="parseVehicleBulkText">
            解析
          </el-button>
          <el-button type="primary" @click="saveVehicleDialog">
            保存
          </el-button>
        </div>

        <div v-if="vehicleDialog.invalidItems.length || vehicleDialog.duplicateItems.length" class="parse-result">
          <el-alert
            v-if="vehicleDialog.invalidItems.length"
            :title="invalidVehicleText"
            type="error"
            :closable="false"
            show-icon
          />
          <el-alert
            v-if="vehicleDialog.duplicateItems.length"
            :title="duplicateVehicleText"
            type="warning"
            :closable="false"
            show-icon
          />
        </div>
      </div>
    </el-dialog>

    <el-dialog v-model="plantDialog.visible" title="添加第 11 位装配厂代码" width="420px">
      <el-form label-position="top">
        <el-form-item label="代码">
          <el-input v-model.trim="plantDialog.code" maxlength="1" placeholder="A" />
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model.trim="plantDialog.name" placeholder="装配厂名称" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="plantDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="addAssemblyPlant">保存</el-button>
      </template>
    </el-dialog>
    </template>

    <VinMaskRulePanel v-else />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import {
  CopyDocument,
  Delete,
  DocumentChecked,
  EditPen,
  Plus,
  Refresh,
  VideoPlay,
} from '@element-plus/icons-vue';

import {
  createFordVinFilterRule,
  deleteFordVinFilterRule,
  getFordVinFilterRules,
  updateFordVinFilterRule,
} from '@/api/users';
import {
  buildVinRegex,
  buildVinRulePayload,
  buildYearCodeEntries,
  defaultEditableVinRule,
  normalizeCodeList,
  normalizeVin,
  ruleToEditableForm,
  sortRulesByPriorityDesc,
  sortVinCodesAscending,
  testVinAgainstRule,
  validateAssemblyPlantCode,
  validateManufacturerCode,
  validateSerial12Code,
  validateVehicleDescriptionCode,
} from '@/utils/fordVinFilterUi.mjs';
import VinMaskRulePanel from '@/components/ford/panels/VinMaskRulePanel.vue';

const activeRuleTab = ref('structured');
const rules = ref([]);
const loading = ref(false);
const saving = ref(false);
const syncCopy = ref(true);
const hasPersistedRules = ref(false);
const builtinTemplateReturned = ref(false);
const editingRuleId = ref('');
const editingPersisted = ref(false);
const manufacturerInput = ref('');
const serial12Input = ref('');
const testVin = ref('LJXCU2BBXTTA12345');
const testResult = ref(null);
const activeGeneratedSections = ref([]);
const activeAdvancedSections = ref([]);

const form = reactive(ruleToEditableForm(defaultEditableVinRule()));

const advanced = reactive({
  showRegexPreview: true,
  showJsonPreview: true,
});

const promptLanguagePattern = /^[A-Z0-9_-]{1,16}$/;
const promptLanguages = [
  { key: 'CN', label: '中文' },
  { key: 'EN', label: 'English' },
];
const promptLanguageKeys = new Set(promptLanguages.map((item) => item.key));

const vehicleDialog = reactive({
  visible: false,
  manufacturerCode: '',
  draftCodes: [],
  newCode: '',
  bulkText: '',
  promptDrafts: {},
  invalidItems: [],
  duplicateItems: [],
});

const plantDialog = reactive({
  visible: false,
  code: '',
  name: '',
});

const listHint = computed(() => {
  if (builtinTemplateReturned.value && !hasPersistedRules.value) {
    return '内置江铃模板尚未落库，保存后写入 Redis。';
  }
  return hasPersistedRules.value ? 'Redis 已保存规则' : '等待保存';
});

const currentPayload = computed(() => buildVinRulePayload(form));
const sortedRules = computed(() => sortRulesByPriorityDesc(rules.value));
const regexPreview = computed(() => buildVinRegex(currentPayload.value));
const jsonPreview = computed(() => JSON.stringify(currentPayload.value, null, 2));
const yearEntries = computed(() => buildYearCodeEntries(form.yearFrom, form.yearTo));
const vehicleDialogTitle = computed(() => `维护 ${vehicleDialog.manufacturerCode} 的第 4-8 位车辆描述码`);
const invalidVehicleText = computed(() => {
  return `非法项：${vehicleDialog.invalidItems.map((item) => `${item.value}（${item.message}）`).join('；')}`;
});
const duplicateVehicleText = computed(() => `重复项：${vehicleDialog.duplicateItems.join('、')}`);
const testResultTagType = computed(() => (testResult.value?.passed ? 'success' : 'danger'));

const vehicleDescriptionRows = computed(() => {
  return form.manufacturerCodes.map((manufacturerCode) => {
    const values = sortVinCodesAscending(form.vehicleDescriptionByManufacturer[manufacturerCode] || []);
    return {
      manufacturerCode,
      count: values.length,
      preview: values.length ? values.slice(0, 8).join('、') : '暂无',
    };
  });
});
const vehiclePromptRows = computed(() => sortVinCodesAscending(vehicleDialog.draftCodes).map((code) => ({ code })));

function listText(value) {
  return Array.isArray(value) && value.length ? value.join('、') : '-';
}

function vehicleDescriptionCount(rule) {
  const map = rule?.vdsAllowListByWmi || {};
  return Object.values(map).reduce((sum, list) => sum + (Array.isArray(list) ? list.length : 0), 0);
}

function responseErrorText(error, fallback) {
  return error?.info || error?.response?.data?.msg || error?.message || fallback;
}

function ensureManufacturerMaps() {
  if (!form.jmcPromptTextByVds || typeof form.jmcPromptTextByVds !== 'object') {
    form.jmcPromptTextByVds = {};
  }

  form.manufacturerCodes.forEach((code) => {
    if (!Array.isArray(form.vehicleDescriptionByManufacturer[code])) {
      form.vehicleDescriptionByManufacturer[code] = [];
    }
    if (!form.jmcPromptTextByVds[code] || typeof form.jmcPromptTextByVds[code] !== 'object') {
      form.jmcPromptTextByVds[code] = {};
    }
  });

  Object.keys(form.vehicleDescriptionByManufacturer).forEach((code) => {
    if (!form.manufacturerCodes.includes(code)) {
      delete form.vehicleDescriptionByManufacturer[code];
    }
  });

  Object.keys(form.jmcPromptTextByVds).forEach((code) => {
    if (!form.manufacturerCodes.includes(code)) {
      delete form.jmcPromptTextByVds[code];
    }
  });
}

function assignForm(nextRule) {
  Object.assign(form, ruleToEditableForm(nextRule));
  ensureManufacturerMaps();
}

function addManufacturerCode() {
  const result = normalizeCodeList([manufacturerInput.value], validateManufacturerCode);
  if (result.invalidItems.length) {
    ElMessage.warning(result.invalidItems[0].message);
    return;
  }
  const code = result.values[0];
  if (!code) {
    ElMessage.warning('请输入厂商识别码');
    return;
  }
  if (form.manufacturerCodes.includes(code)) {
    ElMessage.warning('厂商识别码已存在。');
    return;
  }

  form.manufacturerCodes.push(code);
  form.vehicleDescriptionByManufacturer[code] = [];
  form.jmcPromptTextByVds[code] = {};
  manufacturerInput.value = '';
}

function removeManufacturerCode(code) {
  form.manufacturerCodes = form.manufacturerCodes.filter((item) => item !== code);
  delete form.vehicleDescriptionByManufacturer[code];
  delete form.jmcPromptTextByVds[code];
}

function normalizePromptDrafts(manufacturerCode, codes) {
  const rawMap = form.jmcPromptTextByVds?.[manufacturerCode] || {};
  const result = {};

  codes.forEach((code) => {
    const rawLanguageMap = rawMap[code] || {};
    result[code] = {};
    Object.entries(rawLanguageMap).forEach(([rawLanguage, rawText]) => {
      const language = String(rawLanguage || '').trim().toUpperCase();
      if (promptLanguagePattern.test(language) && promptLanguageKeys.has(language)) {
        result[code][language] = String(rawText || '');
      }
    });
    promptLanguages.forEach(({ key }) => {
      if (result[code][key] == null) {
        result[code][key] = '';
      }
    });
  });

  return result;
}

function ensurePromptDraft(code) {
  if (!vehicleDialog.promptDrafts || typeof vehicleDialog.promptDrafts !== 'object') {
    vehicleDialog.promptDrafts = {};
  }
  if (!vehicleDialog.promptDrafts[code]) {
    vehicleDialog.promptDrafts[code] = {};
  }
  promptLanguages.forEach(({ key }) => {
    if (vehicleDialog.promptDrafts[code][key] == null) {
      vehicleDialog.promptDrafts[code][key] = '';
    }
  });
}

function openVehicleDialog(manufacturerCode, focusBulk = false) {
  vehicleDialog.visible = true;
  vehicleDialog.manufacturerCode = manufacturerCode;
  vehicleDialog.draftCodes = sortVinCodesAscending(form.vehicleDescriptionByManufacturer[manufacturerCode] || []);
  vehicleDialog.newCode = '';
  vehicleDialog.bulkText = focusBulk ? vehicleDialog.draftCodes.join('\n') : '';
  vehicleDialog.promptDrafts = normalizePromptDrafts(manufacturerCode, vehicleDialog.draftCodes);
  vehicleDialog.invalidItems = [];
  vehicleDialog.duplicateItems = [];
}

function addVehicleDialogCode() {
  const result = normalizeCodeList([vehicleDialog.newCode], validateVehicleDescriptionCode);
  if (result.invalidItems.length) {
    ElMessage.warning(result.invalidItems[0].message);
    return;
  }
  const code = result.values[0];
  if (!code) {
    ElMessage.warning('请输入车辆描述码');
    return;
  }
  if (vehicleDialog.draftCodes.includes(code)) {
    ElMessage.warning('车辆描述码已重复。');
    return;
  }
  vehicleDialog.draftCodes.push(code);
  vehicleDialog.draftCodes = sortVinCodesAscending(vehicleDialog.draftCodes);
  ensurePromptDraft(code);
  vehicleDialog.newCode = '';
}

function removeVehicleDialogCode(code) {
  vehicleDialog.draftCodes = vehicleDialog.draftCodes.filter((item) => item !== code);
  delete vehicleDialog.promptDrafts[code];
}

function parseVehicleBulkText() {
  const result = normalizeCodeList(vehicleDialog.bulkText, validateVehicleDescriptionCode);
  const existing = new Set(vehicleDialog.draftCodes);
  const duplicateItems = [...result.duplicateItems];

  result.values.forEach((code) => {
    if (existing.has(code)) {
      if (!duplicateItems.includes(code)) {
        duplicateItems.push(code);
      }
      return;
    }
    existing.add(code);
    vehicleDialog.draftCodes.push(code);
    ensurePromptDraft(code);
  });

  vehicleDialog.draftCodes = sortVinCodesAscending(vehicleDialog.draftCodes);
  vehicleDialog.invalidItems = result.invalidItems;
  vehicleDialog.duplicateItems = duplicateItems;
}

function saveVehicleDialog() {
  const sortedCodes = sortVinCodesAscending(vehicleDialog.draftCodes);
  form.vehicleDescriptionByManufacturer[vehicleDialog.manufacturerCode] = sortedCodes;
  if (!form.jmcPromptTextByVds || typeof form.jmcPromptTextByVds !== 'object') {
    form.jmcPromptTextByVds = {};
  }

  const nextPromptMap = {};
  sortedCodes.forEach((code) => {
    const languageMap = {};
    Object.entries(vehicleDialog.promptDrafts[code] || {}).forEach(([rawLanguage, rawText]) => {
      const language = String(rawLanguage || '').trim().toUpperCase();
      const text = String(rawText || '').trim();
      if (promptLanguagePattern.test(language) && promptLanguageKeys.has(language) && text) {
        languageMap[language] = text;
      }
    });

    if (Object.keys(languageMap).length) {
      nextPromptMap[code] = languageMap;
    }
  });

  if (Object.keys(nextPromptMap).length) {
    form.jmcPromptTextByVds[vehicleDialog.manufacturerCode] = nextPromptMap;
  } else {
    delete form.jmcPromptTextByVds[vehicleDialog.manufacturerCode];
  }
  vehicleDialog.visible = false;
}

function clearVehicleDescriptions(manufacturerCode) {
  form.vehicleDescriptionByManufacturer[manufacturerCode] = [];
  delete form.jmcPromptTextByVds[manufacturerCode];
}

function addSerial12Code() {
  const result = normalizeCodeList([serial12Input.value], validateSerial12Code);
  if (result.invalidItems.length) {
    ElMessage.warning(result.invalidItems[0].message);
    return;
  }
  const code = result.values[0];
  if (!code) {
    ElMessage.warning('请输入流水号首位');
    return;
  }
  if (form.serial12AllowList.includes(code)) {
    ElMessage.warning('流水号首位已存在。');
    return;
  }
  form.serial12AllowList.push(code);
  serial12Input.value = '';
}

function removeSerial12Code(code) {
  form.serial12AllowList = form.serial12AllowList.filter((item) => item !== code);
}

function addAssemblyPlant() {
  const code = normalizeVin(plantDialog.code);
  const name = plantDialog.name.trim();
  const message = validateAssemblyPlantCode(code);

  if (message) {
    ElMessage.warning(message);
    return;
  }
  if (!name) {
    ElMessage.warning('装配厂名称不能为空。');
    return;
  }
  if (form.assemblyPlants.some((item) => item.code === code)) {
    ElMessage.warning('装配厂代码不能重复。');
    return;
  }

  form.assemblyPlants.push({
    code,
    name,
    selected: true,
    builtin: false,
  });
  plantDialog.code = '';
  plantDialog.name = '';
  plantDialog.visible = false;
}

function pickPayload(responseData) {
  if (responseData?.data && typeof responseData.data === 'object') {
    return responseData.data;
  }
  return responseData || {};
}

function selectRule(rule) {
  editingRuleId.value = rule.id || '';
  editingPersisted.value = rule.persisted === true;
  assignForm(rule);
}

function startCreate() {
  editingRuleId.value = '';
  editingPersisted.value = false;
  assignForm({
    ...defaultEditableVinRule(),
    id: 'custom-vin-filter',
    name: '自定义 VIN 过滤规则',
  });
}

function cloneRule(rule) {
  editingRuleId.value = '';
  editingPersisted.value = false;
  assignForm({
    ...rule,
    id: `${rule.id || 'vin-filter'}-copy`,
    name: `${rule.name || rule.id || 'VIN 规则'} 副本`,
    persisted: false,
    builtin: false,
  });
}

async function fetchRules() {
  loading.value = true;
  try {
    const response = await getFordVinFilterRules();
    const payload = pickPayload(response.data);
    rules.value = sortRulesByPriorityDesc(Array.isArray(payload.rules) ? payload.rules : []);
    hasPersistedRules.value = payload.hasPersistedRules === true;
    builtinTemplateReturned.value = payload.builtinTemplateReturned === true;

    if (rules.value.length) {
      const active = editingRuleId.value
        ? rules.value.find((item) => item.id === editingRuleId.value)
        : sortedRules.value[0];
      if (active) {
        selectRule(active);
      }
    } else {
      startCreate();
    }
  } catch (error) {
    ElMessage.error(responseErrorText(error, '获取 VIN 规则失败'));
  } finally {
    loading.value = false;
  }
}

function validateRulePayload(payload) {
  if (!payload.id) {
    return '规则编号不能为空';
  }
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(payload.id)) {
    return '规则编号只能包含字母、数字、下划线和横线';
  }
  if (!payload.name) {
    return '规则名称不能为空';
  }
  if (!payload.wmiAllowList.length) {
    return '第 1-3 位厂商识别码不能为空';
  }
  if (payload.modelYearRange.from < 1980 || payload.modelYearRange.to > 2039 || payload.modelYearRange.from > payload.modelYearRange.to) {
    return '第 10 位年款码年份范围必须在 1980 - 2039 内，且起始年份不能大于结束年份';
  }
  if (!payload.allowAnyVdsWhenListMissing) {
    const emptyCode = payload.wmiAllowList.find((code) => !payload.vdsAllowListByWmi[code]?.length);
    if (emptyCode) {
      return `第 4-8 位车辆描述码不能为空，请为 ${emptyCode} 维护白名单，或在高级设置中允许空列表。`;
    }
  }
  if (!payload.plantAllowList.length) {
    return '第 11 位装配厂代码不能为空';
  }
  if (payload.serial12.enabled && !payload.serial12.allowList.length) {
    return '启用第 12 位限制后，允许字符不能为空';
  }
  try {
    buildVinRegex(payload);
    JSON.stringify(payload);
  } catch (error) {
    return '自动生成正则或 JSON 配置失败';
  }
  return '';
}

async function saveRule() {
  const payload = buildVinRulePayload(form);
  const validation = validateRulePayload(payload);
  if (validation) {
    ElMessage.warning(validation);
    return;
  }

  saving.value = true;
  try {
    const response = editingPersisted.value
      ? await updateFordVinFilterRule(editingRuleId.value, payload, syncCopy.value)
      : await createFordVinFilterRule(payload, syncCopy.value);
    const responsePayload = pickPayload(response.data);
    const savedRule = responsePayload.rule || payload;

    const index = rules.value.findIndex((item) => item.id === savedRule.id);
    if (index >= 0) {
      rules.value.splice(index, 1, savedRule);
    } else {
      rules.value = [savedRule, ...rules.value];
    }
    rules.value = sortRulesByPriorityDesc(rules.value);
    editingRuleId.value = savedRule.id;
    editingPersisted.value = true;
    hasPersistedRules.value = true;
    builtinTemplateReturned.value = false;
    assignForm(savedRule);
    ElMessage.success('VIN 规则已保存');
  } catch (error) {
    ElMessage.error(responseErrorText(error, '保存 VIN 规则失败'));
  } finally {
    saving.value = false;
  }
}

async function deleteRule(rule) {
  if (rule.persisted !== true) {
    ElMessage.warning('内置模板尚未落库');
    return;
  }

  loading.value = true;
  try {
    await deleteFordVinFilterRule(rule.id, syncCopy.value);
    rules.value = rules.value.filter((item) => item.id !== rule.id);
    if (editingRuleId.value === rule.id) {
      if (sortedRules.value.length) {
        selectRule(sortedRules.value[0]);
      } else {
        startCreate();
      }
    }
    ElMessage.success('VIN 规则已删除');
  } catch (error) {
    ElMessage.error(responseErrorText(error, '删除 VIN 规则失败'));
  } finally {
    loading.value = false;
  }
}

function runVinTest() {
  const vin = normalizeVin(testVin.value);
  if (!vin) {
    ElMessage.warning('请输入 17 位 VIN');
    return;
  }
  testVin.value = vin;
  testResult.value = testVinAgainstRule(vin, currentPayload.value);
}

async function copyText(text, message) {
  try {
    await navigator.clipboard.writeText(text);
    ElMessage.success(message);
  } catch (error) {
    ElMessage.error('复制失败');
  }
}

onMounted(() => {
  fetchRules();
});
</script>

<style scoped>
.vin-filter-panel {
  display: grid;
  gap: 14px;
  min-width: 0;
  container-name: vin-filter-panel;
  container-type: inline-size;
}

.panel-header,
.config-section,
.rule-type-switch,
.save-bar {
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(31, 56, 88, 0.05);
}

.rule-type-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
}

.rule-type-switch-copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.rule-type-switch-copy strong {
  color: #132033;
  font-size: 15px;
}

.rule-type-switch-copy span {
  color: #6b7b8f;
  font-size: 12px;
  line-height: 1.45;
}

.rule-type-switch :deep(.el-radio-group) {
  flex: 0 0 auto;
}

.rule-type-switch :deep(.el-radio-button__inner) {
  min-width: 132px;
  font-weight: 700;
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
}

.panel-header h3,
.section-heading h4 {
  margin: 0;
  color: #132033;
  font-weight: 800;
  letter-spacing: 0;
}

.panel-header h3 {
  font-size: 18px;
}

.panel-header p,
.section-heading p {
  margin: 5px 0 0;
  color: #6b7b8f;
  font-size: 13px;
  line-height: 1.45;
}

.header-actions,
.section-actions,
.tag-editor,
.year-controls,
.year-code-strip,
.plant-options,
.test-layout,
.preview-toolbar,
.dialog-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.config-section {
  min-width: 0;
  padding: 16px;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-heading h4 {
  font-size: 16px;
}

.field-grid,
.position-grid,
.advanced-grid {
  display: grid;
  gap: 12px;
}

.field-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.position-grid {
  grid-template-columns: minmax(260px, 0.72fr) minmax(460px, 1.28fr);
  align-items: stretch;
}

.position-card {
  display: flex;
  flex-direction: column;
  min-height: 176px;
}

.position-card-wide {
  grid-column: 1 / -1;
}

.full-control {
  width: 100%;
}

.rule-name-cell {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.rule-name-cell strong,
.rule-name-cell span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rule-name-cell span {
  color: #708095;
  font-size: 12px;
}

.rule-table,
.test-table {
  width: 100%;
}

.rule-table :deep(.el-table__header th),
.test-table :deep(.el-table__header th) {
  background: #f6f9fc !important;
  color: #4b5f75;
  font-weight: 800;
}

.tag-editor {
  min-height: 36px;
}

.field-help {
  margin: 0 0 8px;
  color: #6b7b8f;
  font-size: 13px;
  line-height: 1.45;
}

.compact-tags {
  margin-top: 12px;
}

.tag-input {
  width: 120px;
}

.tag-input-small {
  width: 88px;
}

.readonly-rule {
  padding: 12px;
  border: 1px solid #d8e9fb;
  border-radius: 8px;
  color: #24455f;
  background: #f7fbff;
}

.year-controls {
  margin-bottom: 12px;
}

.year-code-strip {
  align-items: flex-start;
  max-height: 116px;
  overflow: auto;
  padding: 8px;
  border: 1px solid #d8e9fb;
  border-radius: 8px;
  background: #f7fbff;
}

.plant-options {
  align-items: stretch;
}

.plant-empty {
  flex: 1;
  min-height: 106px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #d8e9fb;
  border-radius: 8px;
  background: #f8fbff;
}

.serial12-card,
.serial-tail-card {
  min-height: 164px;
}

.test-layout {
  display: grid;
  grid-template-columns: minmax(220px, 360px) auto;
  margin-bottom: 12px;
}

.error-list,
.parse-result {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}

.preview-toolbar {
  justify-content: space-between;
  margin-bottom: 8px;
  color: #6b7b8f;
  font-size: 13px;
}

.preview-box {
  max-height: 320px;
  overflow: auto;
  margin: 0;
  padding: 12px;
  border: 1px solid #d8e9fb;
  border-radius: 8px;
  color: #132033;
  background: #f8fafc;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
}

.advanced-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.save-bar {
  position: sticky;
  bottom: 0;
  z-index: 3;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
}

.dialog-body {
  display: grid;
  gap: 14px;
}

.prompt-table {
  width: 100%;
}

.prompt-table :deep(.el-textarea__inner) {
  min-height: 64px;
}

.mono {
  font-family: Consolas, Monaco, monospace;
  color: #132033;
}

:deep(.el-input__wrapper),
:deep(.el-textarea__inner) {
  border-radius: 8px;
}

@media (max-width: 1180px) {
  .position-grid,
  .advanced-grid {
    grid-template-columns: 1fr;
  }

  .position-card-wide {
    grid-column: span 1;
  }
}

@media (max-width: 720px) {
  .panel-header,
  .rule-type-switch,
  .section-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .rule-type-switch :deep(.el-radio-group) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .rule-type-switch :deep(.el-radio-button),
  .rule-type-switch :deep(.el-radio-button__inner) {
    width: 100%;
  }

  .field-grid,
  .test-layout {
    grid-template-columns: 1fr;
  }

  .save-bar {
    position: static;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}

@container vin-filter-panel (max-width: 720px) {
  .rule-type-switch {
    align-items: stretch;
    flex-direction: column;
  }

  .rule-type-switch :deep(.el-radio-group) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .rule-type-switch :deep(.el-radio-button),
  .rule-type-switch :deep(.el-radio-button__inner) {
    width: 100%;
  }
}
</style>
